
// Cart.js
/* global globalThis */
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Divider,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  CartWrapper,
  CartCard,
  CartHeader,
  CartItems,
  CartEmptyState,
  CartFooter,
  TotalRow,
  TotalLabel,
  TotalValue,
  CompleteButton,
} from './styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CartItem from './CartItem';

const Cart = ({
  cartItems = [],
  onUpdateQty,
  onRemoveItem,
  onCompleteSale,
  loading = false,
}) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [billDiscountPercent, setBillDiscountPercent] = useState("0");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  
  // ✅ cash received + confirm
  const [cashReceived, setCashReceived] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const makeIdempotencyKey = () =>
  (globalThis.crypto?.randomUUID?.() ||
   `K-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const getSaleLineName = (med, productName) => {
    const pn = (productName ?? '').trim();
    if (pn) return pn;

    if (!med) return 'Item';

    const rawBrand = (med.brandName ?? '').trim();
    const generic = (med.genericName ?? '').trim();

    const brandIsStarsOnly = rawBrand !== '' && /^[*\s]+$/.test(rawBrand);

    if (!rawBrand || brandIsStarsOnly) {
      if (generic) return generic;
      return rawBrand || 'Item';
    }

    return rawBrand;
  };

  // subtotal (after item-level discounts)
  const subTotal = useMemo(
    () =>
      cartItems.reduce((sum, item) => {
        const rawUnitPrice = Number(item.retailPrice ?? 0);
        const discount = Number(item.discount ?? 0);
        const qty = Number(item.qty ?? 1);

        const hasDiscount = discount > 0 && item.discountApplied;
        const unitPriceAfterDiscount = hasDiscount
          ? rawUnitPrice * (1 - discount / 100)
          : rawUnitPrice;

        const lineTotal = unitPriceAfterDiscount * qty;
        return sum + lineTotal;
      }, 0),
    [cartItems]
  );

  // bill-level discount amount
  const billDiscountAmount = useMemo(() => {
    const p = parseFloat(billDiscountPercent) || 0;
    if (p <= 0) return 0;
    return subTotal * (p / 100);
  }, [subTotal, billDiscountPercent]);

  // final grand total
  const total = useMemo(
    () => Math.max(0, subTotal - billDiscountAmount),
    [subTotal, billDiscountAmount]
  );

  
  // ✅ cash change calc (only meaningful for cash)
  const cashReceivedNum = useMemo(() => {
    const n = Number(String(cashReceived).trim());
    return Number.isFinite(n) ? n : 0;
  }, [cashReceived]);

  const changeDue = useMemo(() => {
    if (paymentMethod !== 'cash') return 0;
    if (cashReceived === '') return 0; // if they didn't enter anything, don't show weird negatives
    return cashReceivedNum - total;
  }, [paymentMethod, cashReceived, cashReceivedNum, total]);

  const isCashShort = useMemo(() => {
    if (paymentMethod !== 'cash') return false;
    if (cashReceived === '') return false;
    return cashReceivedNum < total;
  }, [paymentMethod, cashReceived, cashReceivedNum, total]);

  const canCompleteCash =
  paymentMethod !== 'cash' ||
  (cashReceived !== '' && !isCashShort);

  // const canCompleteNonCash =
  // paymentMethod === 'cash' ||
  // confirmOpen;

  const canCompleteSale =
  cartItems.length &&
  !loading &&
  canCompleteCash 
  // &&
  // canCompleteNonCash
  ;

  useEffect(() => {
  if (cartItems.length === 0) setIdempotencyKey("");
  else if (!idempotencyKey) setIdempotencyKey(makeIdempotencyKey());
}, [cartItems.length, idempotencyKey]);

  // ✅ if payment method changes, reset confirm + cash input
  useEffect(() => {
    setConfirmOpen(false);
    setCashReceived('');
  }, [paymentMethod]);

  // ✅ if cart changes, close confirm (prevents confirming stale cart)
  useEffect(() => {
    setConfirmOpen(false);
  }, [cartItems]);

  const buildSalePayload = () => {

    const refNo = `INV-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

    const items = cartItems.map((item) => {
      const rawUnitPrice = Number(item.retailPrice ?? 0);
      const discount = Number(item.discount ?? 0);
      const qty = Number(item.qty ?? 1);

      const hasDiscount = discount > 0 && item.discountApplied;
      const unitPriceAfterDiscount = hasDiscount
        ? rawUnitPrice * (1 - discount / 100)
        : rawUnitPrice;

      const lineTotal = unitPriceAfterDiscount * qty;

      // ✅ FIX: name source is now item.itemId
      const med = item?.itemId?.medicineId || {};
      const productName = item?.itemId?.name || '';

      const safeName = getSaleLineName(med, productName);

      return {
        inventoryId: item._id,
        batchNo: item.batchNo,

        medicineName: safeName,
        brandName: med.brandName ?? '',
        genericName: med.genericName ?? '',

        unitPrice: rawUnitPrice,
        discount: hasDiscount ? discount : 0,
        discountApplied: hasDiscount,
        unitPriceAfterDiscount,
        qty,
        lineTotal,
      };
    });

    const subTotalLocal = items.reduce((s, i) => s + i.lineTotal, 0);
    const p = parseFloat(billDiscountPercent) || 0;
    const billDiscountAmountLocal = p > 0 ? subTotalLocal * (p / 100) : 0;
    const grandTotal = Math.max(0, subTotalLocal - billDiscountAmountLocal);

    const payload = {
      idempotencyKey, 
      refNo,
      paymentMethod,
      items,
      subTotal: subTotalLocal,
      billDiscountPercent: p,
      billDiscountAmount: billDiscountAmountLocal,
      total: grandTotal,
    };

    // ✅ optional cash info (doesn't break backend if ignored)
    if (paymentMethod === 'cash' && cashReceived !== '') {
      payload.cashReceived = cashReceivedNum;
      payload.changeDue = cashReceivedNum - grandTotal;
    }

    return payload;
  };

  const handleComplete = () => {
  if (!cartItems.length || loading) return;

  if (!idempotencyKey) {
    setIdempotencyKey(makeIdempotencyKey());
    return; // next click will use the key
  }

  // ✅ CASH: no confirmation step
  if (paymentMethod === 'cash') {
    if (isCashShort) return; // block if they entered cash and it's short

    if (typeof onCompleteSale === 'function') {
      const payload = buildSalePayload();
      console.log("🧾 FINAL SALE PAYLOAD:", payload);
      onCompleteSale(payload);
      setConfirmOpen(false);
      setCashReceived('');
    }
    return;
  }

  // ✅ CARD/OTHER: 1st click opens confirm, 2nd click confirms
  if (!confirmOpen) {
    setConfirmOpen(true);
    return;
  }

  if (typeof onCompleteSale === 'function') {
    const payload = buildSalePayload();
    console.log("🧾 FINAL SALE PAYLOAD:", payload);
    onCompleteSale(payload);
    setConfirmOpen(false);
    setCashReceived('');
  }
};

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  const cashNotes = [100, 500, 1000, 2000, 5000];

  return (
    <CartWrapper>
      <CartCard elevation={0}>
        <CartHeader>
          <Typography variant="h6" sx={{ fontWeight: 600, marginLeft: '5px' }}>
            Cart
          </Typography>
        </CartHeader>

        <CartItems>
          {cartItems.length === 0 ? (
            <CartEmptyState>
              <ShoppingCartOutlinedIcon sx={{ fontSize: 200, color: '#ededed' }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 18, color: 'lightgrey' }}
              >
                Cart is empty
              </Typography>
            </CartEmptyState>
          ) : (
            cartItems.map((item) => (
              <CartItem
                key={`${item._id}-${item.batchNo}`}
                item={item}
                onUpdateQty={(item, newQty, extra = {}) => {
                  onUpdateQty(item, newQty, extra);
                }}
                onRemoveItem={onRemoveItem}
              />
            ))
          )}
        </CartItems>

        <CartFooter>
          <Divider />

          <TotalRow>
            <TotalLabel>Payment Method</TotalLabel>
            <TextField
              select
              size="small"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                width: '100px',
              }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </TotalRow>

          <TotalRow>
            <TotalLabel>Sub Total:</TotalLabel>
            <Typography sx={{ fontWeight: '600' }}>Rs {subTotal.toFixed(2)}</Typography>
          </TotalRow>

          <TotalRow>
            <TotalLabel>Bill Discount</TotalLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize:{xs:'12px'} }}>
                − Rs {billDiscountAmount.toFixed(2)}
              </Typography>

              <TextField
                size="small"
                type="number"
                value={billDiscountPercent}
                onFocus={() => {
                  if (billDiscountPercent === '0') setBillDiscountPercent('');
                }}
                onChange={(e) => {
                  let val = e.target.value;

                  if (val === '') {
                    setBillDiscountPercent('');
                    return;
                  }

                  let num = Number(val);
                  if (Number.isNaN(num)) return;

                  if (num < 0) num = 0;
                  if (num > 100) num = 100;

                  num = Math.round(num * 10) / 10;
                  setBillDiscountPercent(String(num));
                }}
                onBlur={() => {
                  if (billDiscountPercent === '' || isNaN(Number(billDiscountPercent))) {
                    setBillDiscountPercent('0');
                  }
                }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  },
                }}
              />
            </Box>
          </TotalRow>
              {paymentMethod === 'cash' && <Divider sx={{ my: 1.25 }} />}
          <TotalRow>
            <TotalLabel sx={{ color: '#2563eb', fontSize: '18px' }}>Total:</TotalLabel>
            <TotalValue>Rs {total.toFixed(2)}</TotalValue>
          </TotalRow>

          {/* ✅ Cash received + notes (only for cash) — placed AFTER Total */}
          {paymentMethod === 'cash' && (
            <>
              <TotalRow>
                <TotalLabel>Cash Received</TotalLabel>
                <TextField
                  size="small"
                  type="number"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  sx={{
                    width: 130,
                    '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  }}
                  slotProps={{
                    inputProps: { min: 0, step: 1 },
                    startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                  }}
                />
              </TotalRow>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  mt:1,
                  mb: 1,
                }}
              >
                {cashNotes.map((n) => (
                  <Button
                    key={n}
                    size="small"
                    variant="outlined"
                    onClick={() => setCashReceived(String(n))}
                    sx={{ borderRadius: '5px', minWidth: 0, px: 1.25 }}
                  >
                    {n}
                  </Button>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setCashReceived(total.toFixed(2))}
                  sx={{ borderRadius: '5px', minWidth: 0, px: 1.25, textTransform:'capitalize' }}
                >
                  Exact
                </Button>
              </Box>

              {/* Change / Short */}
              {cashReceived !== '' && (
                <TotalRow>
                  <TotalLabel sx={{ fontWeight: 700 }}>
                    {changeDue >= 0 ? 'Change' : 'Short'}
                  </TotalLabel>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: changeDue >= 0 ? '#16a34a' : '#dc2626',
                    }}
                  >
                    Rs {Math.abs(changeDue).toFixed(2)}
                  </Typography>
                </TotalRow>
              )}
            </>
          )}

          {/* ✅ inline confirmation block (small, above button) */}
          {confirmOpen && paymentMethod !== 'cash' && (
            <Box
              sx={{
                mt: 1.25,
                mb: 1,
                p: 1.25,
                borderRadius: '12px',
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Confirm sale?</Typography>

              <Typography variant="body2" sx={{ color: '#475569' }}>
                {paymentMethod === 'cash'
                  ? isCashShort
                    ? 'Cash received is less than total. Fix it or clear the cash field for exact cash.'
                    : 'Make sure cash is received before confirming.'
                  : 'Make sure the payment is approved before confirming.'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleCancelConfirm}
                  disabled={loading}
                >
                  Cancel
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  onClick={handleComplete}
                  disabled={
                    loading ||
                    !cartItems.length ||
                    (paymentMethod === 'cash' && isCashShort)
                  }
                  sx={{ backgroundColor: '#16a34a' }}
                >
                  Confirm
                </Button>
              </Box>
            </Box>
          )}

          <CompleteButton
            variant="contained"
            fullWidth
            disabled={!canCompleteSale}
            onClick={handleComplete}
            sx={{ backgroundColor: '#16a34a', marginTop:'25px' }}
          >
            {loading ? 'Processing…' : confirmOpen ? 'Confirm Sale' : 'Complete Sale'}
          </CompleteButton>
        </CartFooter>
      </CartCard>
    </CartWrapper>
  );
};

export default Cart;
